/**
 * Folder archives (docs/folder-archives.md): a folder on a computer whose oldest files move to the cloud
 * when it passes an age or size limit, and are removed from the computer only after their cloud copy has
 * been read back and checked. The rules here are shared by the API (to refuse a bad folder), the portal
 * (to grey out folders in the picker) and, restated in Go, the agent (which checks again before acting).
 */
export const ARCHIVE_UNITS = ['file', 'item'];
export const ARCHIVE_MIN_BYTES = 1024 ** 3; // 1 GB
export const ARCHIVE_MAX_BYTES = 1024 ** 5; // 1 PB, a typo guard
export const ARCHIVE_MIN_AGE_DAYS = 1;
export const ARCHIVE_MAX_AGE_DAYS = 36_500;
/** "Keep on this computer" choices offered when files go back where they were. null = until released. */
export const ARCHIVE_HOLD_CHOICES = [7, 30, 90, null];
export const ARCHIVE_DEFAULT_HOLD_DAYS = 30;
const WIN_SYSTEM = ['windows', 'program files', 'program files (x86)', 'programdata', '$recycle.bin', 'system volume information', 'recovery', 'boot', 'perflogs'];
const POSIX_SYSTEM = ['/bin', '/boot', '/dev', '/etc', '/lib', '/lib32', '/lib64', '/libx32', '/opt', '/proc', '/run', '/sbin', '/snap', '/sys', '/usr', '/var', '/tmp'];
/**
 * A computer's own path (`C:\Users\tim\Desktop\Weddings`, `/home/tim/Pictures`) as a backup path
 * (`/C:/Users/tim/Desktop/Weddings`). Returns null for anything that is not an absolute local path.
 */
export function nativeToVpath(native, style) {
    const raw = native.trim();
    if (!raw || raw.includes('\0'))
        return null;
    if (style === 'windows') {
        if (raw.startsWith('\\\\') || raw.startsWith('//'))
            return null; // UNC: not local
        const m = /^([A-Za-z]):(?:[\\/]+(.*))?$/.exec(raw);
        if (!m)
            return null;
        const parts = (m[2] ?? '').split(/[\\/]+/).filter(Boolean);
        if (parts.some((p) => p === '.' || p === '..' || /[<>:"|?*]/.test(p)))
            return null;
        return `/${m[1].toUpperCase()}:` + parts.map((p) => '/' + p).join('');
    }
    if (!raw.startsWith('/'))
        return null;
    const parts = raw.split('/').filter(Boolean);
    if (parts.some((p) => p === '.' || p === '..'))
        return null;
    return '/' + parts.join('/');
}
/** Comparison form of a backup path: Windows paths compare case-insensitively. */
function cmp(vpath) {
    return /^\/[A-Za-z]:(\/|$)/.test(vpath) || vpath.startsWith('/UNC/') ? vpath.toLowerCase() : vpath;
}
/** True when `p` is `root` itself or lies below it (backup paths). */
export function vpathUnder(p, root) {
    const a = cmp(p);
    const b = cmp(root);
    return a === b || a.startsWith(b === '/' ? '/' : b + '/');
}
/**
 * Why a folder cannot be an archive, or null when it can. `others` are the paths of the computer's other
 * active archives (the one being edited excluded).
 */
export function archiveFolderProblem(vpath, style, others = []) {
    if (!vpath.startsWith('/') || vpath.includes('\0') || (vpath !== '/' && vpath.endsWith('/')))
        return 'invalid';
    const parts = vpath.split('/').filter(Boolean);
    if (parts.some((p) => p === '.' || p === '..'))
        return 'invalid';
    if (style === 'windows') {
        if (parts[0] === 'UNC')
            return 'network';
        if (!parts[0] || !/^[A-Za-z]:$/.test(parts[0]))
            return 'invalid';
        if (parts.length === 1)
            return 'root';
        const top = parts[1].toLowerCase();
        if (WIN_SYSTEM.includes(top))
            return 'system';
        if (top === 'users') {
            if (parts.length <= 3)
                return 'profile_root'; // C:\Users or C:\Users\tim
            // C:\Users\tim\AppData holds programs' settings, never a person's files.
            if (parts[3].toLowerCase() === 'appdata')
                return 'system';
        }
    }
    else {
        if (parts.length === 0)
            return 'root';
        const top = '/' + parts[0];
        if (POSIX_SYSTEM.includes(top))
            return 'system';
        if (top === '/home' && parts.length <= 2)
            return 'profile_root';
        if (top === '/root' && parts.length === 1)
            return 'profile_root';
        if (top === '/home' && parts[2]?.startsWith('.'))
            return 'system';
    }
    if (parts.some((p) => p.toLowerCase() === 'zerogap'))
        return 'app_data';
    for (const o of others) {
        if (vpathUnder(vpath, o))
            return 'inside_other';
        if (vpathUnder(o, vpath))
            return 'contains_other';
    }
    return null;
}
/** Plain-language explanation of a folder problem, for the portal and API errors. */
export const ARCHIVE_FOLDER_PROBLEM_TEXT = {
    invalid: 'This is not a folder on the computer',
    network: 'Folders on another computer or a network drive cannot be archived',
    root: 'A whole drive cannot be archived; choose a folder inside it',
    system: 'Windows and program folders cannot be archived',
    profile_root: "A person's whole user folder cannot be archived; choose a folder inside it, such as Desktop or Pictures",
    app_data: "ZeroGap's own folder cannot be archived",
    inside_other: 'This folder is inside another archive on this computer',
    contains_other: 'This folder contains another archive on this computer',
};
/**
 * True when an archived item is held on the computer by a "keep on this computer" hold: the hold names
 * the item, something inside it, or a folder it is in. Used by the agent (restated in Go) and the portal.
 */
export function holdCovers(itemPath, holdPath) {
    return vpathUnder(itemPath, holdPath) || vpathUnder(holdPath, itemPath);
}
//# sourceMappingURL=folder-archive.js.map